"use client";

import { captureException } from "@slider/logger";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

import { SliderWordmark } from "@/components/brand/wordmark";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

type ErrorPageProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const [eventId, setEventId] = useState<string | undefined>(error.digest);

  useEffect(() => {
    const id = captureException(error);
    if (id) setEventId(id);
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col p-6">
      <Link href="/" aria-label="Slider home" className="inline-flex w-fit">
        <SliderWordmark />
      </Link>
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        <Empty className="max-w-md flex-none border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <AlertTriangle />
            </EmptyMedia>
            <EmptyTitle>Something went wrong on our end</EmptyTitle>
            <EmptyDescription>
              We&apos;ve been notified and are looking into it. Try again in a moment, or head back
              home.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <div className="flex gap-2">
              <Button onClick={reset}>
                <RefreshCcw />
                Try again
              </Button>
              <Button variant="outline" asChild>
                <Link href="/">Go home</Link>
              </Button>
            </div>
          </EmptyContent>
        </Empty>
        {process.env.NODE_ENV !== "production" && (
          <details className="w-full max-w-md">
            <summary className="text-muted-foreground cursor-pointer text-xs">
              Error details (dev only)
            </summary>
            <pre className="bg-muted text-muted-foreground mt-2 max-h-64 overflow-auto rounded p-3 text-xs whitespace-pre-wrap">
              {error.stack ?? error.message}
            </pre>
          </details>
        )}
      </div>
      {eventId && (
        <p className="text-muted-foreground/60 text-center font-mono text-[10px]">ref: {eventId}</p>
      )}
    </div>
  );
}
